/**
 * Generated by orval v8.5.3 🍺
 * Do not edit manually.
 * Metapic Social Media API Documentation
 * <p>Welcome to the API documentation for integrating with Metapic's Social Media Platform.</p>
            <h2>Authentication</h2>
            <p>This API uses <strong>Bearer Token</strong> authentication. You must include a valid token in the <strong>Authorization</strong> header with every request to access protected endpoints.</p>
            <h4>Example:</h4>
            <pre>Authorization: Bearer <i>your_token_here</i></pre>
            <p>*Replace <i>your_token_here</i> with your actual Bearer token. All API requests require this token for authentication.</p>
 * OpenAPI spec version: 1.0
 */
import * as axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'

import type {
  CreateUserProfileDto,
  GetOAuthUrlParams,
  ListUserProfilesParams,
  OAuthUrlResponse,
  OauthCallbackParams,
  Platform,
  UpdateUserProfileDto,
  UserProfile,
} from '../metapicSocialMediaAPIDocumentation.schemas'

export const getUserProfiles = (axiosInstance: AxiosInstance = axios.default) => {
  /**
   * Returns the OAuth authorization URL to start a new platform connection
   * @summary Get OAuth URL
   */
  const getOAuthUrl = (
    platform: Platform,
    params?: GetOAuthUrlParams,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<OAuthUrlResponse>> => {
    return axiosInstance.get(`/v1/user-profiles/${platform}/oauth-url`, {
      ...options,
      params: { ...params, ...options?.params },
    })
  }
  /**
   * Redirect endpoint called by the OAuth provider after authorization. Exchanges the code for an access token and creates a connected user profile.
   * @summary OAuth callback
   */
  const oauthCallback = (
    platform: Platform,
    params: OauthCallbackParams,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<void>> => {
    return axiosInstance.get(`/v1/user-profiles/${platform}/oauth-callback`, {
      ...options,
      params: { ...params, ...options?.params },
    })
  }
  /**
   * Returns all connected social profiles for the given user
   * @summary List user profiles
   */
  const listUserProfiles = (
    userId: string,
    params?: ListUserProfilesParams,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<UserProfile[]>> => {
    return axiosInstance.get(`/v1/users/${userId}/user-profiles`, {
      ...options,
      params: { ...params, ...options?.params },
    })
  }
  /**
   * Creates a new claimed user profile entry for the given platform handle
   * @summary Add a user profile
   */
  const createHandle = (
    userId: string,
    createUserProfileDto: CreateUserProfileDto,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<UserProfile>> => {
    return axiosInstance.post(`/v1/users/${userId}/user-profiles`, createUserProfileDto, options)
  }
  /**
   * Update user profile properties
   * @summary Update user profile
   */
  const updateUserProfile = (
    userId: string,
    id: string,
    updateUserProfileDto: UpdateUserProfileDto,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<UserProfile>> => {
    return axiosInstance.patch(
      `/v1/users/${userId}/user-profiles/${id}`,
      updateUserProfileDto,
      options,
    )
  }
  /**
   * Deletes the user profile relation for the given user.
   * @summary Delete user profile
   */
  const _delete = (
    userId: string,
    id: string,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<void>> => {
    return axiosInstance.delete(`/v1/users/${userId}/user-profiles/${id}`, options)
  }
  /**
   * Revokes the OAuth integration for the given user profile by clearing the stored token and resetting the state to verified. The user profile entry is not removed — only the direct platform integration is disabled.
   * @summary Disconnect user profile
   */
  const disconnect = (
    userId: string,
    id: string,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<void>> => {
    return axiosInstance.delete(`/v1/users/${userId}/user-profiles/${id}/disconnect`, options)
  }
  return {
    getOAuthUrl,
    oauthCallback,
    listUserProfiles,
    createHandle,
    updateUserProfile,
    _delete,
    disconnect,
  }
}
export type GetOAuthUrlResult = AxiosResponse<OAuthUrlResponse>
export type OauthCallbackResult = AxiosResponse<void>
export type ListUserProfilesResult = AxiosResponse<UserProfile[]>
export type CreateHandleResult = AxiosResponse<UserProfile>
export type UpdateUserProfileResult = AxiosResponse<UserProfile>
export type _DeleteResult = AxiosResponse<void>
export type DisconnectResult = AxiosResponse<void>
